import { AnimatePresence, motion } from "framer-motion";
import { Fragment, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/utils/cn";
import { navListVariants } from "./sidebar.constants";
import {
  getModuleNavTabs,
  getModuleSettingsLink,
} from "@/config/nav-tabs.config";
import { usePermissions } from "@/hooks/usePermissions";
import { useSidebarNavigation } from "./useSidebarNavigation";
import { SidebarFrame } from "./SidebarFrame";
import { SpaceSwitcher } from "./SpaceSwitcher";
import { NavItem } from "./NavItem";
import { NavAccordionItem } from "./NavAccordionItem";
import { useLocale } from "@/hooks/useLocale";

interface ISidebarProps {
  isSidebarOpen: boolean;
  /** Whether the viewport is desktop-width — determines overlay vs. in-flow. */
  isDesktop: boolean;
  onClose: () => void;
}

export const Sidebar = ({
  isSidebarOpen,
  isDesktop,
  onClose,
}: ISidebarProps) => {
  const { t } = useTranslation("common");
  const { byDir } = useLocale();
  const dirSign = byDir(1, -1);
  const { can } = usePermissions();

  const {
    currentPath,
    currentSearch,
    isCollapsed,
    isOverlay,
    search,
    setSearch,
    query,
    showSwitcher,
    activeSpace,
    filteredItems,
    groupedItems,
    showGroupHeadings,
    openItemId,
    setOpenItemId,
    handleLinkClick,
    handleSpaceSwitch,
  } = useSidebarNavigation(isSidebarOpen, isDesktop, onClose);

  /**
   * The SAME function is passed to every `NavAccordionItem` (see the render
   * below) — it takes the item's id rather than being bound per-item, which
   * is what makes it possible for this to be one stable reference at all.
   *
   * That stability matters: `NavAccordionItem`'s auto-expand effect depends
   * on this callback's identity (`[isModuleActive, onOpenChange]`). A fresh
   * per-item closure (e.g. `(open) => setOpenItemId(open ? item.id : null)`
   * written inline in the `.map()`) is a new function on every render of
   * `Sidebar`, so that effect re-ran on every render regardless of whether
   * anything relevant changed — and because `isModuleActive` stays `true`
   * for as long as you're on that module's page, every one of those
   * spurious re-runs called `onOpenChange(true)` again. That silently undid
   * a manual chevron collapse a frame after it happened: click to close, the
   * item's OWN stale-effect re-open fired immediately after and reopened it,
   * so collapsing the active module's accordion never visibly worked.
   * `useCallback` with a stable `[setOpenItemId]` dep makes this one function
   * for the lifetime of the component, so the effect only re-runs when
   * `isModuleActive` (or the id) actually changes.
   */
  const handleAccordionOpenChange = useCallback(
    (id: number, open: boolean) => {
      setOpenItemId((current) => {
        if (open) return id;
        // A close request only takes effect while THIS item is still the
        // open one — see the matching note on `NavAccordionItem`'s deferred
        // self-close, which is the other caller of this same path.
        return current === id ? null : current;
      });
    },
    [setOpenItemId],
  );

  return (
    <SidebarFrame
      isSidebarOpen={isSidebarOpen}
      isCollapsed={isCollapsed}
      isOverlay={isOverlay}
      onClose={onClose}
      label={t("header.main_navigation")}
      navLabel={
        activeSpace === "company"
          ? t("common:a11y.company_navigation")
          : t("common:a11y.my_space_navigation")
      }
      header={
        showSwitcher && (
          <SpaceSwitcher
            activeSpace={activeSpace}
            isCollapsed={isCollapsed}
            onSwitch={handleSpaceSwitch}
          />
        )
      }
      search={{ value: search, onChange: setSearch }}
    >
      <AnimatePresence mode="wait" initial={false} custom={dirSign}>
        <motion.ul
          key={activeSpace}
          custom={dirSign}
          variants={navListVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className={cn(
            "flex flex-col gap-0.5 lg:gap-1",
            isCollapsed ? "p-1.5 lg:p-2" : "p-2 lg:p-3",
          )}
        >
          {groupedItems.map((group) => (
            <Fragment key={group.id ?? "uncategorized"}>
              {showGroupHeadings && (
                <li
                  className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground first:pt-1"
                  aria-hidden="true"
                >
                  {group.name}
                </li>
              )}
              {group.items.map((item) => {
                // Nested accordion only when expanded and the module has
                // hardcoded page tabs OR a dedicated settings page;
                // otherwise a plain (leaf) nav link.
                const tabs = isCollapsed
                  ? undefined
                  : getModuleNavTabs(item.url);
                // The trailing settings row shows only when the user holds
                // the module's `settings` permission — otherwise the row (and
                // the settings-only accordion) is hidden entirely.
                const settingsLinkRaw = isCollapsed
                  ? undefined
                  : getModuleSettingsLink(item.url);
                const settingsLink =
                  settingsLinkRaw && can(settingsLinkRaw.section, "settings")
                    ? settingsLinkRaw
                    : undefined;
                return (
                  <li key={item.id}>
                    {(tabs && tabs.length > 0) || settingsLink ? (
                      <NavAccordionItem
                        item={item}
                        tabs={tabs ?? []}
                        settingsLink={settingsLink}
                        currentPath={currentPath}
                        currentSearch={currentSearch}
                        onLinkClick={handleLinkClick}
                        isOpen={openItemId === item.id}
                        onOpenChange={handleAccordionOpenChange}
                      />
                    ) : (
                      <NavItem
                        item={item}
                        isCollapsed={isCollapsed}
                        currentPath={currentPath}
                        onLinkClick={handleLinkClick}
                      />
                    )}
                  </li>
                );
              })}
            </Fragment>
          ))}
          {!isCollapsed && query && filteredItems.length === 0 && (
            <li className="px-2 py-6 text-center text-xs text-muted-foreground">
              {t("spaces.no_modules_found")}
            </li>
          )}
        </motion.ul>
      </AnimatePresence>
    </SidebarFrame>
  );
};
